import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';

import {ClientMemoryApi} from '../../../api/clientMemoryApi';
import {isOk} from '../../../utils/apiResponse';
import {AutonomyRaiseOfferModal} from './AutonomyRaiseOfferModal';
import {MemoryAutonomyControl} from './MemoryAutonomyControl';
import {useAutonomyRaiseOffer} from './useAutonomyRaiseOffer';
import {useKnobRefocus} from './useKnobRefocus';
import {useClientMemoryAutonomy} from './useMemoryAutonomy';

/**
 * El knob «¿Cuánto aprueba Dashbo solo?» de un cliente, para el marco CLÁSICO
 * (`MemoryFactsView`, que es un class component y no puede usar el hook).
 *
 * No es un duplicado del rail del Recorrido: es la misma pieza (`MemoryAutonomyControl`) en la otra
 * superficie. El rail sólo existe bajo `.ui-v2`, así que sin esto una agencia de tier IA —o
 * cualquiera que volvió al Dashbo clásico— tendría la cola de curado y ninguna forma de mover su
 * gate, que es exactamente el problema que la card viene a resolver.
 *
 * Se dibuja arriba de la cola y NO dentro de ella: con la cola vacía el control tiene que seguir
 * estando, porque es donde se decide que siga vacía.
 *
 * La oferta de aprobar lo que ya estaba esperando (card 8uu3IdNd) va también acá y no sólo en el
 * Recorrido: es una consecuencia de mover el knob, y el knob vive en las dos superficies. Lo único
 * que cambia es CÓMO se aprueba —acá por la API y recargando, allá por el store del mazo—; cuándo
 * se ofrece y cuántos son lo decide el mismo hook.
 */
export function ClientMemoryAutonomyPanel({clientId, onFactsApproved}) {
    const {t} = useTranslation();
    // El nivel EFECTIVO, no el override crudo: desde el 08/09 el knob es por cliente y no hay
    // interruptor de herencia que dibujar (card 8uu3IdNd).
    const {effective, available, loading, saving, setValue, canSetFull} =
        useClientMemoryAutonomy(clientId);
    const offer = useAutonomyRaiseOffer(clientId);
    // El riel, para devolverle el foco al cerrar el modal — sólo si el cambio vino del teclado. El
    // helper es UNO para las dos superficies del knob: ver `useKnobRefocus`.
    const {knobRef, refocusKnob, rememberOrigin} = useKnobRefocus();

    // El nivel anterior se lee ANTES de guardar; después ya es el nuevo. Y sólo se ofrece si el
    // guardado entró de verdad (`setValue` resuelve a un booleano).
    const change = useCallback((next, meta) => {
        const previous = effective;
        rememberOrigin(meta);
        return Promise.resolve(setValue(next))
            .then((saved) => (saved ? offer.askAfterChange(previous, next) : undefined));
    }, [effective, setValue, offer, rememberOrigin]);

    // ⚠️ **Devuelve un booleano y refresca sólo si el bulk entró.** Esto era `.then(() => …)` sin
    // mirar la respuesta, y el `APIClient` **resuelve** (no rechaza) con `{status: 'ERROR' | …}`
    // —además el backend manda el código TIPADO en `status`, así que la rama de error se escribe
    // `!== 'OK'`, que es lo que `isOk` hace—. Consecuencia: una tanda rechazada entera disparaba el
    // refetch y cerraba el modal como si hubiera funcionado. El booleano es el contrato que
    // `useAutonomyRaiseOffer.accept` necesita para avisar (hallazgo C-knob-09).
    // `skipMissing`: la otra superficie de la oferta tiene la MISMA carrera entre el GET que calcula
    // los ids y este POST, así que también pide tolerancia. Sin eso un hecho borrado en otra pestaña
    // convierte «aprobar 45» en cero aprobados (C-knob-08).
    //
    // ⚠️ Y **lee `skipped`** (C-knob2-03, review del 26/09): pedir tolerancia y tomar cualquier `isOk`
    // como éxito total devolvía el silencio por otra puerta. Con 45 ids y 2 salteados no había aviso;
    // con los 45 salteados el refresh no cambiaba nada y se leía como «ya quedó». El mazo de la 2.1 sí
    // lo avisaba, así que la misma operación contaba dos historias según la superficie.
    const approve = useCallback((factIds) => ClientMemoryApi
        .bulkReviewFacts(clientId, 'approve', factIds, {skipMissing: true})
        .then((response) => {
            if (!isOk(response)) return false;
            const skipped = Number(response.skipped) || 0;
            const entered = Math.max(0, factIds.length - skipped);
            // El refetch va igual con 0 aprobados: los salteados ya no están en la cola y la pantalla
            // tiene que dejar de mostrarlos.
            if (onFactsApproved) onFactsApproved();
            // El éxito TOTAL también avisa: el modal se cerraba y sólo cambiaban los números, mientras
            // la acción hermana del mazo sí avisa «Se aprobaron N hechos» (QA-VIS-knob2-06, 27/09).
            // Y el parcial usa el texto de la OFERTA, que no afirma dónde quedó lo que no entró: a un
            // salteado lo resolvió otra persona, así que «sigue en la cola» era falso (QA-knob2-05).
            toast.info(skipped > 0
                ? t('memory_autonomy.raise_offer_partial', {n: entered, total: factIds.length})
                : t('ia_booster.deck.toast_approved_all', {count: entered}));
            // `false` deja la oferta ABIERTA, y con 0 entrados eso sería pedirle al usuario que
            // reintente algo que ya está resuelto. Se cierra y lo explica el aviso de arriba.
            return true;
        }), [clientId, onFactsApproved, t]);

    // Mientras carga no se dibuja nada: un esqueleto arriba de la cola empujaría las cartas hacia
    // abajo justo cuando el usuario va a apretar la primera.
    if (loading || !available) return null;

    return (
        <div className="cmem-autonomy">
            <MemoryAutonomyControl
                value={effective}
                canSetFull={canSetFull}
                saving={saving}
                onChange={change}
                knobRef={knobRef}
            />
            <AutonomyRaiseOfferModal
                offer={offer.offer}
                busy={offer.busy}
                // El foco vuelve al riel, pero sólo si la oferta se cerró: con el bulk rechazado el
                // modal queda abierto para reintentar (QA-VIS-knob2-09).
                onYes={() => offer.accept(approve).then((closed) => { if (closed) refocusKnob(); })}
                onNo={() => { offer.dismiss(); refocusKnob(); }}
            />
        </div>
    );
}

export default ClientMemoryAutonomyPanel;
